/**
 * marketplace/models.ts — types for Module #10, the Roamer Marketplace™.
 *
 * A platform MODULE, not a core engine. A curated marketplace of trusted
 * products and services for remote workers — NOT a general shopping
 * platform: every listing solves a real problem for WorkRoamers.
 *
 * Honesty rules as everywhere: partners exist only as recorded/curated
 * listings (future partner APIs sync real catalogs — nothing fabricated);
 * community ratings derive from recorded Roamer Reports™ only (null when
 * none); the Roamer Match Score™ normalizes over evaluable factors only
 * and is null when nothing can be evaluated or personalization is off.
 * Future commerce (booking, affiliates, checkout) is interfaces only —
 * NO payment processing.
 */

// ─── Categories ────────────────────────────────────────────────────────────────

export const MARKETPLACE_CATEGORIES = [
  'travelInsurance',
  'healthInsurance',
  'esimProvider',
  'vpnService',
  'coworkingMembership',
  'airportTransfer',
  'storageService',
  'mailForwarding',
  'taxProfessional',
  'immigrationService',
  'legalService',
  'translationService',
  'languageLearning',
  'bankingPartner',
  'moneyTransfer',
  'accounting',
  'travelGear',
  'laptopRepair',
  'photographyService',
  'businessFormation',
  'virtualMailbox',
  'remoteWorkSoftware',
  'cloudStorage',
  'aiProductivityTool',
  /** Placeholder for future partner categories. */
  'futurePartner',
] as const;
export type MarketplaceCategory = (typeof MARKETPLACE_CATEGORIES)[number];

// ─── Partner profile ───────────────────────────────────────────────────────────

export interface PartnerPricing {
  /** Numeric amount when recorded — else null (never guessed). */
  amount: number | null;
  currency: string | null;
  period: 'oneTime' | 'monthly' | 'yearly' | null;
  /** Free-text summary, e.g. "from $42/mo". */
  summary: string | null;
}

export interface ContactMethod {
  kind: 'email' | 'phone' | 'chat' | 'form';
  value: string;
}

export interface MarketplacePartner {
  id: string;
  name: string;
  category: MarketplaceCategory;
  description: string;
  /** Empty means coverage unknown — NOT worldwide. */
  supportedCountries: string[];
  pricing: PartnerPricing;
  /** Verified partner status — curation-owned, defaults false. */
  verified: boolean;
  availableLanguages: string[];
  contactMethods: ContactMethod[];
  website: string | null;
  /** null = unknown, never assumed friendly. */
  remoteWorkerFriendly: boolean | null;
  availabilityNote: string | null;
  addedAt: number;
}

// ─── Community reviews (Roamer Reports™) ──────────────────────────────────────

export const MARKETPLACE_REPORT_KINDS = [
  'report',
  'recommendation',
  'successStory',
  'tip',
  'warning',
] as const;
export type MarketplaceReportKind = (typeof MARKETPLACE_REPORT_KINDS)[number];

export interface MarketplaceReport {
  id: string;
  partnerId: string;
  kind: MarketplaceReportKind;
  text: string;
  /** 1–5 or null (tips/warnings often carry no rating). */
  rating: number | null;
  /** Only verified WorkRoamer members may submit — enforced at add. */
  submittedByVerifiedMember: true;
  helpfulVotes: number;
  recordedAt: number;
}

// ─── Roamer Match Score™ ──────────────────────────────────────────────────────

export const MATCH_FACTORS = [
  'location',
  'preferences',
  'budget',
  'travelStyle',
  'communityFeedback',
  'previousUsage',
  'language',
  'currentNeeds',
] as const;
export type MatchFactor = (typeof MATCH_FACTORS)[number];

export interface MatchScoreBreakdown {
  factor: MatchFactor;
  /** 0–100 contribution for this factor. */
  score: number;
  /** Recorded evidence for the factor — never empty. */
  basis: string;
}

export interface RoamerMatchScore {
  partnerId: string;
  /** 0–100 normalized over EVALUATED factors only; null when none evaluable. */
  score: number | null;
  breakdown: MatchScoreBreakdown[];
  evaluatedFactors: number;
}

// ─── User preferences & usage ──────────────────────────────────────────────────

export interface MarketplacePreferences {
  /** Monthly budget for services, in budgetCurrency. */
  monthlyBudget: number | null;
  budgetCurrency: string | null;
  languages: string[];
  travelStyle: 'budget' | 'balanced' | 'comfort' | null;
  /** Categories the user currently needs — drives recommendations. */
  currentNeeds: MarketplaceCategory[];
}

export const DEFAULT_MARKETPLACE_PREFERENCES: MarketplacePreferences = {
  monthlyBudget: null,
  budgetCurrency: null,
  languages: [],
  travelStyle: null,
  currentNeeds: [],
};

/** Recorded previous usage of a partner — feeds the match score. */
export interface UsageRecord {
  id: string;
  partnerId: string;
  outcome: 'positive' | 'neutral' | 'negative';
  note: string | null;
  recordedAt: number;
}

// ─── Smart recommendations ─────────────────────────────────────────────────────

/** Category bundle suggested on arrival in a new country. */
export const ARRIVAL_CATEGORIES: MarketplaceCategory[] = [
  'esimProvider',
  'airportTransfer',
  'travelInsurance',
  'translationService',
];

export interface SmartRecommendation {
  id: string;
  partnerId: string;
  partnerName: string;
  category: MarketplaceCategory;
  message: string;
  /** Recorded evidence backing the recommendation. Never empty. */
  basis: string[];
}

// ─── Favorites & collections ───────────────────────────────────────────────────

export interface ServiceCollection {
  id: string;
  name: string;
  partnerIds: string[];
  createdAt: number;
}

/** Side-by-side comparison row — recorded fields only. */
export interface PartnerComparison {
  partnerId: string;
  name: string;
  category: MarketplaceCategory;
  pricingSummary: string | null;
  verified: boolean;
  communityRating: number | null;
  reviewCount: number;
  supportedCountries: string[];
  availableLanguages: string[];
}

// ─── Future commerce (interfaces ONLY — no payment processing) ─────────────────

export interface AffiliatePartnershipRef {
  id: string;
  partnerId: string;
  program: string;
  /** Disclosure is mandatory for transparency. */
  disclosure: string;
}

export interface BookingRequestRef {
  id: string;
  partnerId: string;
  status: 'draft' | 'requested' | 'confirmed' | 'cancelled';
  note: string | null;
  createdAt: number;
}

export interface ReferralRef {
  id: string;
  partnerId: string;
  code: string;
  recordedAt: number;
}

export interface PromoCodeRef {
  id: string;
  partnerId: string;
  code: string;
  description: string | null;
  expiresAt: number | null;
}

/** FUTURE partner API integrations — registry only, never called. */
export interface PartnerApiConnector {
  id: string;
  name: string;
}

// ─── Privacy ───────────────────────────────────────────────────────────────────

export interface MarketplacePrivacySettings {
  /** Off ⇒ match scores and smart recommendations return null/empty. */
  personalizedRecommendations: boolean;
  marketingOptIn: boolean;
  /** Gates sharing/exporting anything toward partners. */
  partnerDataSharing: boolean;
  /** Gates saved services (favorites/collections) in the export. */
  shareSavedServices: boolean;
  /** Gates usage/purchase history in the export. */
  sharePurchaseHistory: boolean;
}

export const DEFAULT_MARKETPLACE_PRIVACY: MarketplacePrivacySettings = {
  personalizedRecommendations: false,
  marketingOptIn: false,
  partnerDataSharing: false,
  shareSavedServices: false,
  sharePurchaseHistory: false,
};

// ─── Discovery & export ────────────────────────────────────────────────────────

export interface MarketplaceFilters {
  /** Matches partners supporting the user's current country. */
  currentBaseOnly?: boolean;
  country?: string;
  category?: MarketplaceCategory;
  verifiedOnly?: boolean;
  /** Partners whose recorded community rating is ≥ this (unrated excluded). */
  minCommunityRating?: number;
  /** Partners whose recorded pricing amount is ≤ this (unpriced excluded). */
  maxPrice?: number;
  language?: string;
  /** Partners with a recorded availability note. */
  availableOnly?: boolean;
  /** remoteWorkerFriendly === true only — unknown never passes. */
  remoteWorkerFriendlyOnly?: boolean;
}

export interface MarketplaceExport {
  exportedAt: number;
  partners: MarketplacePartner[];
  reports: MarketplaceReport[];
  /** Gated on shareSavedServices. */
  favorites: string[];
  collections: ServiceCollection[];
  /** Gated on sharePurchaseHistory. */
  usage: UsageRecord[];
  /** Gated on partnerDataSharing. */
  preferences: MarketplacePreferences | null;
}
